import React, { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import {
  KeyRound, Loader2, CheckCircle2, XCircle, ShieldAlert,
  Eye, EyeOff, Trash2, PlugZap, MessageCircle, Mail, ArrowUpRight,
  Home, MessageSquare,
} from "lucide-react";

const PROVIDER_META = {
  composio: {
    title: "Composio",
    subtitle: "Publicaciones reales en Instagram, Facebook y TikTok",
    icon: PlugZap,
    color: "#6366F1",
    signupUrl: "https://app.composio.dev",
    fields: [
      { key: "api_key", label: "COMPOSIO_API_KEY", placeholder: "comp_xxxxxxxxxxxxxxx", required: true, sensitive: true },
    ],
    docs: "Genere su API key en Composio → Settings → API Keys. Además, en el dashboard de Composio debe crear los Auth Configs por plataforma (ac_instagram, ac_facebook, ac_tiktok).",
    unlocks: "Publicaciones REALES en Instagram, Facebook y TikTok desde /conectividad (botón 'Conectar cuenta real'). Sin esta llave todas las cuentas quedan en modo SIMULADO.",
  },
  whatsapp: {
    title: "WhatsApp Cloud API",
    subtitle: "Envío de plantillas aprobadas vía Meta Graph API",
    icon: MessageCircle,
    color: "#25D366",
    signupUrl: "https://developers.facebook.com/docs/whatsapp/cloud-api/get-started",
    fields: [
      { key: "access_token", label: "ACCESS_TOKEN (permanente)", placeholder: "EAAG...", required: true, sensitive: true },
      { key: "phone_number_id", label: "PHONE_NUMBER_ID", placeholder: "1234567890", required: true, sensitive: false },
    ],
    docs: "Requiere plantillas aprobadas por Meta para envíos business-initiated. En pruebas, el sistema usa la plantilla `hello_world` (idioma es). Puede indicar otra plantilla en el contenido: {{template:mi_template}}",
    unlocks: "Envío REAL de plantillas y mensajes de WhatsApp desde los módulos Redes Sociales y Messenger. Sin esta llave WhatsApp queda simulado.",
  },
  resend: {
    title: "Resend",
    subtitle: "Envío real de campañas email desde su dominio",
    icon: Mail,
    color: "#00A36C",
    signupUrl: "https://resend.com",
    fields: [
      { key: "api_key", label: "RESEND_API_KEY", placeholder: "re_xxxxxxxxxx", required: true, sensitive: true },
      { key: "from_email", label: "Remitente (from)", placeholder: "Marketing <no-reply@sudominio.com>", required: false, sensitive: false },
    ],
    docs: "Requiere dominio verificado en Resend. El pixel de apertura sigue siendo propio (se inyecta automáticamente).",
    unlocks: "Envío REAL de campañas de Email Marketing desde el dominio de la agencia + tracking de aperturas/clics. Sin esta llave los envíos son simulados (solo tracking interno).",
  },
  mercadolibre: {
    title: "MercadoLibre Venezuela",
    subtitle: "Publicación de avisos inmobiliarios vía API oficial (MLV)",
    icon: Home,
    color: "#FFE600",
    signupUrl: "https://developers.mercadolibre.com.ve",
    fields: [
      { key: "client_id", label: "ML_CLIENT_ID", placeholder: "1234567890", required: true, sensitive: false },
      { key: "client_secret", label: "ML_CLIENT_SECRET", placeholder: "secret_xxxxxxxxxxxxx", required: true, sensitive: true },
    ],
    docs: "Cree una app en DevCenter de MercadoLibre → Mis aplicaciones. Site: Venezuela (MLV). Cada empresa hará OAuth por separado desde su módulo MercadoLibre.",
    unlocks: "Publicación REAL de avisos inmobiliarios en MercadoLibre Venezuela. Con esta llave, cada empresa puede autorizar su propia cuenta ML desde /conectividad.",
  },
  meta_app: {
    title: "Meta App (Messenger)",
    subtitle: "Recepción de mensajes Messenger vía webhooks",
    icon: MessageSquare,
    color: "#1877F2",
    signupUrl: "https://developers.facebook.com/apps",
    fields: [
      { key: "app_id", label: "APP_ID", placeholder: "1234567890", required: true, sensitive: false },
      { key: "app_secret", label: "APP_SECRET", placeholder: "xxxxxxxxxxxxx", required: true, sensitive: true },
      { key: "verify_token", label: "VERIFY_TOKEN (personalizado)", placeholder: "fstudy_2026", required: true, sensitive: true },
    ],
    docs: "En su Meta App → Messenger → Webhooks: Callback URL = <su-backend>/api/messenger/webhook, Verify Token = el que ponga aquí. Suscriba el evento `messages` de las páginas. Cada empresa configura su page_access_token en su conexión de Facebook.",
    unlocks: "Bandeja REAL de Messenger por página de Facebook. Sin esta llave las conversaciones de Messenger quedan en modo simulado.",
  },
};

const STATUS_STYLES = {
  not_configured: { label: "No configurada", cls: "bg-stone-100 text-stone-600 border-stone-300", Icon: KeyRound },
  configured:     { label: "Configurada",    cls: "bg-brand-emerald-soft text-brand-emerald-dark border-brand-emerald/40", Icon: CheckCircle2 },
  error:          { label: "Error",          cls: "bg-red-100 text-red-700 border-red-300", Icon: XCircle },
};

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }); }
  catch { return iso; }
}

function ProviderCard({ provider, state, onSaved, onDeleted, onTested }) {
  const meta = PROVIDER_META[provider];
  const Icon = meta?.icon;
  const status = state?.status || "not_configured";
  const st = STATUS_STYLES[status] || STATUS_STYLES.not_configured;
  const StIcon = st.Icon;

  const [editing, setEditing] = useState(status === "not_configured");
  const [formValues, setFormValues] = useState(
    Object.fromEntries((meta?.fields || []).map((f) => [f.key, ""]))
  );
  const [revealed, setRevealed] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (status === "not_configured") setEditing(true);
  }, [status]);

  // Defensa: si el provider no está declarado en PROVIDER_META, no renderizamos.
  // Se coloca DESPUÉS de todos los hooks para no violar Rules of Hooks.
  if (!meta) return null;

  async function handleSave() {
    const required = meta.fields.filter((f) => f.required).map((f) => f.key);
    for (const r of required) {
      if (!formValues[r]?.trim()) {
        toast.error(`Debe indicar ${meta.fields.find((f) => f.key === r).label}`);
        return;
      }
    }
    if (mounted.current) setSaving(true);
    try {
      const body = Object.fromEntries(
        Object.entries(formValues).filter(([, v]) => v && v.trim())
      );
      await api.put(`/integrations/${provider}`, body);
      toast.success(`${meta.title} guardado`);
      if (mounted.current) {
        setFormValues(Object.fromEntries(meta.fields.map((f) => [f.key, ""])));
        setEditing(false);
      }
      await onSaved();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo guardar");
    } finally { if (mounted.current) setSaving(false); }
  }

  async function handleTest() {
    if (mounted.current) setTesting(true);
    try {
      const res = await api.post(`/integrations/${provider}/test`);
      if (res.data.ok) toast.success(`${meta.title}: conexión OK`);
      else toast.error(`${meta.title}: ${res.data.error || "falló"}`);
      await onTested();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fallo al probar");
    } finally { if (mounted.current) setTesting(false); }
  }

  async function handleDelete() {
    if (!window.confirm(`¿Eliminar la configuración de ${meta.title}? Las credenciales cifradas se borrarán.`)) return;
    if (mounted.current) setDeleting(true);
    try {
      await api.delete(`/integrations/${provider}`);
      toast.success(`${meta.title} eliminado`);
      await onDeleted();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo eliminar");
    } finally { if (mounted.current) setDeleting(false); }
  }

  return (
    <Card className="p-6 space-y-5 card-elevated" data-testid={`integration-card-${provider}`}>
      <div className="flex items-start gap-4">
        <div
          className="h-12 w-12 rounded-xl flex items-center justify-center text-white shrink-0"
          style={{ backgroundColor: meta.color }}
        >
          <Icon className="h-6 w-6" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <h3 className="font-serif-display text-2xl text-brand-ink font-semibold">{meta.title}</h3>
            <Badge className={`border font-semibold ${st.cls}`} data-testid={`integration-status-${provider}`}>
              <StIcon className="h-3.5 w-3.5 mr-1" />
              <span>{st.label}</span>
            </Badge>
          </div>
          <p className="text-sm text-stone-500 mt-1">{meta.subtitle}</p>
          {meta.unlocks && (
            <p className="text-[11px] text-brand-emerald-dark mt-2 font-medium" data-testid={`provider-unlocks-${provider}`}>
              <span className="uppercase tracking-widest font-semibold text-stone-500 mr-1">Qué desbloquea:</span>
              {meta.unlocks}
            </p>
          )}
        </div>
        <a
          href={meta.signupUrl} target="_blank" rel="noreferrer"
          className="hidden sm:inline-flex items-center gap-1 text-xs text-brand-emerald-dark hover:underline"
        >
          Obtener llave <ArrowUpRight className="h-3.5 w-3.5" />
        </a>
      </div>

      {status !== "not_configured" && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-400">Credenciales</div>
            <div className="mt-1 space-y-1">
              {meta.fields.map((f) => (
                <div key={f.key} className="flex items-center gap-2">
                  <span className="text-xs text-stone-500 w-32 shrink-0">{f.label}</span>
                  <code className="text-xs font-mono bg-stone-100 rounded px-2 py-0.5" data-testid={`integration-mask-${provider}-${f.key}`}>
                    {state?.last4?.[f.key] || "—"}
                  </code>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-stone-400">Última prueba</div>
            <div className="mt-1 text-sm text-brand-ink" data-testid={`integration-last-test-${provider}`}>
              <span>{fmt(state?.last_test_at)}</span>
            </div>
            {state?.last_test_error && (
              <div className="mt-1 text-xs text-red-600 line-clamp-3" data-testid={`integration-error-${provider}`}>
                <span>{state.last_test_error}</span>
              </div>
            )}
            <div className="mt-2 text-xs text-stone-500">
              <span>Configurada por </span>
              <span>{state?.configured_by_name || "—"}</span>
              <span> · </span>
              <span>{fmt(state?.configured_at)}</span>
            </div>
          </div>
        </div>
      )}

      {editing ? (
        <div className="border border-dashed border-brand-emerald/40 rounded-lg p-4 space-y-3 bg-brand-emerald-soft/20">
          <p className="text-xs text-stone-600">{meta.docs}</p>
          {meta.fields.map((f) => (
            <div key={f.key} className="space-y-1">
              <Label className="text-xs">{f.label}{f.required && <span className="text-red-500">*</span>}</Label>
              <div className="relative">
                <Input
                  type={f.sensitive && !revealed[f.key] ? "password" : "text"}
                  value={formValues[f.key]}
                  onChange={(e) => setFormValues((v) => ({ ...v, [f.key]: e.target.value }))}
                  placeholder={f.placeholder}
                  data-testid={`integration-input-${provider}-${f.key}`}
                  autoComplete="off"
                />
                {f.sensitive && (
                  <button
                    type="button"
                    onClick={() => setRevealed((r) => ({ ...r, [f.key]: !r[f.key] }))}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-400 hover:text-brand-ink"
                    aria-label="Mostrar/ocultar"
                  >
                    {revealed[f.key] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                )}
              </div>
            </div>
          ))}
          <div className="flex flex-wrap gap-2 pt-2">
            <Button
              onClick={handleSave} disabled={saving}
              className="btn-primary"
              data-testid={`integration-save-${provider}`}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
              Guardar credenciales
            </Button>
            {status !== "not_configured" && (
              <Button variant="outline" onClick={() => setEditing(false)}>Cancelar</Button>
            )}
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          <Button
            onClick={handleTest} disabled={testing}
            variant="outline"
            data-testid={`integration-test-${provider}`}
          >
            {testing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
            Probar conexión
          </Button>
          <Button
            onClick={() => setEditing(true)}
            variant="outline"
            data-testid={`integration-rotate-${provider}`}
          >
            Rotar llave
          </Button>
          <Button
            onClick={handleDelete} disabled={deleting}
            variant="outline"
            className="text-red-600 hover:text-red-700"
            data-testid={`integration-delete-${provider}`}
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Eliminar
          </Button>
        </div>
      )}
    </Card>
  );
}

export default function Integraciones() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    try {
      const res = await api.get("/integrations");
      setProviders(res.data.providers || []);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "No se pudo cargar");
    } finally { setLoading(false); }
  }

  useEffect(() => { refresh(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stateMap = Object.fromEntries(providers.map((p) => [p.provider, p]));

  return (
    <div className="space-y-6" data-testid="integrations-page">
      <div className="flex items-start gap-4 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="h-1 w-16 bg-brand-gold mb-4 rounded-full" />
          <h1 className="font-serif-display text-3xl sm:text-4xl text-brand-ink font-semibold">Integraciones</h1>
          <p className="text-stone-600 mt-2 max-w-2xl">
            Configure las llaves de los proveedores reales. Las llaves se guardan <b>cifradas en reposo</b>
            (Fernet + ENCRYPTION_KEY). El sistema solo muestra los últimos 4 caracteres.
            Sin llaves configuradas, todo sigue funcionando en <b>modo simulado</b>.
          </p>
        </div>
        <div className="flex items-start gap-2 text-xs text-stone-500 max-w-sm bg-brand-gold-soft/40 border border-brand-gold/40 rounded-lg p-3">
          <ShieldAlert className="h-4 w-4 text-brand-gold-dark shrink-0 mt-0.5" />
          <span>
            Solo el rol <b>Master</b> puede ver y modificar esta página. Cada cambio queda registrado en
            Auditoría (sin registrar la llave).
          </span>
        </div>
      </div>

      {loading ? (
        <div className="card-elevated p-16 text-center">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-brand-emerald" />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {["composio", "whatsapp", "resend", "mercadolibre", "meta_app"].map((p) => (
            <ProviderCard
              key={p}
              provider={p}
              state={stateMap[p]}
              onSaved={refresh}
              onDeleted={refresh}
              onTested={refresh}
            />
          ))}
        </div>
      )}
    </div>
  );
}
